import { HumusAuflage } from 'src/app/data-model';

/**
 * Die Humusauflage steht als eigene Zeilengruppe oben in der Horizontfolge,
 * ueber der Gelaendeoberflaeche. Gespeichert wird sie aber weiter in den drei
 * festen Feldern `extendedHeader.humusAuflage`; die Zeilen sehen nur aus wie
 * Horizonte.
 *
 * Warum nicht als echte Horizontzeilen: Filter ("Humusauflage(Ol)"),
 * Excel-Beleg (C6/C7/C8), Zeilen-Export, Import und Ausdruck lesen genau diese
 * drei Felder. In einer freien Zeilenliste wuerde ein Vertipper ("O1" statt
 * "Ol") den Wert stillschweigend aus allen diesen Auswertungen fallen lassen,
 * und die Liste liesse zwei Ol-Zeilen oder eine vertauschte Reihenfolge zu.
 */
export interface AuflageZeile {
	/** Feldname fuer Kommentare, Hilfe und Validierung, woertlich wie vor dem Umzug. */
	feld: 'Ol' | 'Of' | 'Oh';
	/** Schluessel im gespeicherten Datensatz. */
	schluessel: keyof HumusAuflage;
}

/** Reihenfolge von oben nach unten, wie im Gelaende angesprochen. */
export const AUFLAGE_ZEILEN: AuflageZeile[] = [
	{ feld: 'Ol', schluessel: 'ol' },
	{ feld: 'Of', schluessel: 'of' },
	{ feld: 'Oh', schluessel: 'oh' },
];

/** Die drei Feldnamen, z.B. fuer die Kommentar-Zeile unter der Zeilengruppe. */
export const AUFLAGE_FELDNAMEN: string[] = AUFLAGE_ZEILEN.map(z => z.feld);

/**
 * Ueberschrift ueber den drei Zeilen. Sie sagt, was die Tiefen-Spalte hier
 * NICHT sagen kann.
 *
 * Bis zum 10.09.2026 stand statt dessen ein Minus vor der Maechtigkeit
 * ("liegt ueber 0 cm"). Das war an der falschen Spalte: Eine Maechtigkeit ist
 * nie negativ - 1 cm Streu sind 1 cm. Ein Minus gehoert an eine Tiefe, und
 * ausgerechnet die Tiefen-Spalte blieb hier leer. Die Zeile las sich dadurch
 * wie ein Fehler (Nutzer-Befund 10.09.2026). Die Tiefen-Spalte bleibt
 * absichtlich leer: Ueber der Gelaendeoberflaeche wird keine Tiefe gemessen,
 * sondern eine Dicke.
 *
 * Nur in der EINGABEMASKE. Die Uebersicht traegt sie seit 15.09.2026 nicht
 * mehr (Nutzer-Wunsch): Mit Eintraegen sagen Ol/Of/Oh selbst, was sie sind,
 * und ohne Eintraege steht „Auflagehorizonte" in der Platzhalter-Zeile.
 */
export const AUFLAGE_UEBERSCHRIFT = 'Humusauflage';

/**
 * Gegenstueck in der Substratfolge. Die Auflage hat keine Bodenart - dort ist
 * nichts zu erfassen, der Platz wird nur benannt und freigehalten. Die Zelle
 * traegt zusaetzlich die Gesamt-Auflage (`auflagePlatzhalterText`) - in der
 * Uebersicht und seit 15.09.2026 auch in der Eingabemaske.
 */
export const AUFLAGE_PLATZHALTER = 'organische Auflage';

/** Summe der drei Maechtigkeiten in cm; leere und unsinnige Werte zaehlen als 0. */
export function auflageGesamt(auflage?: HumusAuflage | null): number {
	if (!auflage) return 0;
	let summe = 0;
	for (const zeile of AUFLAGE_ZEILEN) {
		const wert = auflage[zeile.schluessel] as number | undefined;
		if (typeof wert === 'number' && Number.isFinite(wert) && wert > 0) summe += wert;
	}
	return Math.round((summe + Number.EPSILON) * 100) / 100;
}

/**
 * „Tiefe bis" je Auflagezeile, wenn die Auflage zum Profil gehoert - bei den
 * Filz-Bodenformen (bohrpunkte/filz-bodenform.ts). Dort liegt 0 cm an der
 * Oberkante des Ol, und jede Zeile endet bei der Summe der Maechtigkeiten bis
 * dorthin: Ol bei ol, Of bei ol + of, Oh bei ol + of + oh. Nur Anzeige - die
 * drei Felder bleiben, was sie sind (Maechtigkeiten).
 *
 * Eine Zeile ohne Maechtigkeit bekommt keine Tiefe: Sie ist nicht da. Die
 * Zeilen darunter zaehlen trotzdem weiter, sie liegen deshalb nicht hoeher.
 * Eine 0 ist ein Eintrag (siehe `hatAuflage`) und bekommt ihre Tiefe.
 */
export function auflageTiefeBis(auflage: HumusAuflage | null | undefined, schluessel: keyof HumusAuflage): number | undefined {
	if (!auflage) return undefined;
	let summe = 0;
	for (const zeile of AUFLAGE_ZEILEN) {
		const wert = auflage[zeile.schluessel] as number | undefined;
		const erfasst = typeof wert === 'number' && Number.isFinite(wert) && wert >= 0;
		if (erfasst) summe += wert;
		if (zeile.schluessel === schluessel) {
			return erfasst ? Math.round((summe + Number.EPSILON) * 100) / 100 : undefined;
		}
	}
	return undefined;
}

/**
 * Ist ueberhaupt eine Auflage erfasst?
 *
 * Die Uebersicht zeigt die drei Zeilen nur dann. Ohne Eintrag stuenden dort
 * drei leere Zeilen, an denen sich nichts ablesen laesst; dann benennen die
 * Platzhalter den Platz. In der Eingabemaske stehen die drei Zeilen dagegen
 * IMMER - dort will man ja etwas eintragen koennen.
 *
 * **Eine 0 IST ein Eintrag** (Nutzer-Entscheidung 11.09.2026): Wer 0 cm
 * hinschreibt, hat gemessen und nichts gefunden - das ist eine Aussage, keine
 * Luecke. Erfasst ist deshalb alles, was eine Zahl ist; leer bleibt leer.
 */
export function hatAuflage(auflage?: HumusAuflage | null): boolean {
	if (!auflage) return false;
	return AUFLAGE_ZEILEN.some(z => Number.isFinite(auflage[z.schluessel] as number));
}

/**
 * „6 cm" - oder nichts, wenn keine Auflage erfasst ist. Das gemeinsame Stueck
 * von Trennlinie (Eingabemaske) und Platzhalter (Uebersicht).
 *
 * Ohne Eintrag bleibt es leer: „0 cm" laese sich wie eine Messung, obwohl
 * schlicht nichts dasteht.
 */
export function auflageSummeKurz(auflage?: HumusAuflage | null): string {
	if (!hatAuflage(auflage)) return '';
	return `${auflageGesamt(auflage).toString().replace('.', ',')} cm`;
}

/**
 * „Auflage 6 cm" - oder nichts, wenn keine erfasst ist.
 *
 * Ohne Eintrag bleibt es leer: „Auflage 0 cm" laese sich wie eine Messung,
 * obwohl schlicht nichts dasteht.
 */
export function auflageSummeText(auflage?: HumusAuflage | null): string {
	const kurz = auflageSummeKurz(auflage);
	return kurz ? `Auflage ${kurz}` : '';
}

/**
 * Platzhalter der Substratseite (Uebersicht UND Eingabemaske), mit der
 * Gesamt-Auflage: „organische Auflage · 6 cm" - ohne Eintrag nur
 * „organische Auflage".
 *
 * Bis zum 15.09.2026 stand die Summe in der Uebersicht in einer eigenen Zeile
 * unter der Trennlinie („Auflage 6 cm"). Die Zeile trug sonst nichts; am
 * Platzhalter steht die Summe bei dem, was sie beschreibt, und die Zeile
 * faellt weg (Nutzer-Wunsch 15.09.2026). Die Trennlinie ist seitdem die
 * Unterkante des Auflage-Blocks.
 */
export function auflagePlatzhalterText(auflage?: HumusAuflage | null): string {
	const kurz = auflageSummeKurz(auflage);
	return kurz ? `${AUFLAGE_PLATZHALTER} · ${kurz}` : AUFLAGE_PLATZHALTER;
}

/** Beschriftung der Trennlinie in der Eingabemaske. */
export const GELAENDEOBERFLAECHE_TEXT = 'Geländeoberfläche 0 cm';

/**
 * Trennlinie zwischen Auflage und Mineralboden in der EINGABEMASKE: dort steht
 * das Wort, denn die drei Zeilen darueber sind Eingabefelder und tragen keine
 * Erklaerung. In der Uebersicht sagen stattdessen die Platzhalter ober- und
 * unterhalb, was wo liegt, und die Summe steht am Platzhalter der
 * Substratseite (`auflagePlatzhalterText`).
 */
export function gelaendeoberflaecheText(auflage?: HumusAuflage | null): string {
	const summe = auflageSummeText(auflage);
	return summe ? `${GELAENDEOBERFLAECHE_TEXT} · ${summe}` : GELAENDEOBERFLAECHE_TEXT;
}
